import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { isApiError, toMessage } from "@/api/errors";
import { createInvite, listEmployeesFull } from "@/api/lookups";
import type { InviteCreate, InviteResponse, Role, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FolderPicker } from "@/components/folder/FolderPicker";
import { useFolders } from "@/hooks/useFolders";
import { cn } from "@/lib/utils";

const ROLES: Role[] = ["admin", "manager", "user"];

type EmployeeMode = "new" | "existing";

const schema = z
  .object({
    mode: z.enum(["new", "existing"]),
    email: z.string().email(),
    role: z.enum(["admin", "manager", "user"]),
    // Existing-employee branch.
    employee_id: z.string().optional(),
    // New-employee branch.
    full_name: z.string().optional(),
    position: z.string().optional(),
    department: z.string().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.mode === "new") {
      if (v.full_name === undefined || v.full_name.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["full_name"],
          message: "required",
        });
      }
    } else if (v.employee_id === undefined || v.employee_id.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["employee_id"],
        message: "required",
      });
    }
  });

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  mode: "new",
  email: "",
  role: "user",
  employee_id: "",
  full_name: "",
  position: "",
  department: "",
};

export interface InviteFormDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Optional callback fired immediately after a successful invite create.
   * The admin Users registry uses this to invalidate its ``users`` query
   * so the freshly minted invite appears as a pending row right away.
   * Kept optional so consumers that don't care about the registry don't
   * have to pass a no-op.
   */
  onCreated?: () => void;
}

async function copyToClipboard(value: string): Promise<boolean> {
  // navigator.clipboard only works in secure contexts (https:// or localhost).
  // For plain http on an IP/hostname we fall back to a transient textarea +
  // document.execCommand("copy"), which works everywhere modern browsers run.
  try {
    if (
      typeof window !== "undefined" &&
      window.isSecureContext &&
      navigator?.clipboard?.writeText !== undefined
    ) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to execCommand fallback
  }
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

/**
 * Map the two `POST /api/invites` 409 codes to inline-friendly Russian
 * copy. Returns an empty string for anything else so the caller can fall
 * back to the generic ``toMessage`` rendering.
 *
 * Kept module-private (not exported) — the only consumer is this dialog
 * and we don't want callers reaching past ``mutation.error`` for the same
 * decision elsewhere; the AcceptInviteForm has its own mapper because the
 * status-code surface there is different (410 expired, 404 not found, …).
 */
function mapCreateInviteError(err: unknown, t: (k: string) => string): string {
  if (!isApiError(err)) return "";
  if (err.code === "employee_already_linked") {
    return t("adminUsers.invite.errors.employeeAlreadyLinked");
  }
  if (err.code === "employee_invite_pending") {
    return t("adminUsers.invite.errors.employeeInvitePending");
  }
  return "";
}

/**
 * Build a copy-paste-ready invite link for the operator. Uses the current
 * browser origin when available (FE talks to the same host the operator is
 * looking at) and falls back to a relative path for SSR/test contexts.
 */
export function buildInviteLink(token: string, origin?: string): string {
  const o =
    origin ?? (typeof window !== "undefined" ? window.location.origin : "");
  if (o.length === 0) return `/accept-invite/${encodeURIComponent(token)}`;
  return `${o.replace(/\/+$/, "")}/accept-invite/${encodeURIComponent(token)}`;
}

/**
 * Translate the vetted form values into the `POST /api/invites` body
 * (TM-1ac64335). Exactly one of `employee_id` | `employee` is populated;
 * the other is `null` so the backend's "exactly one" guard is satisfied.
 */
function toInvitePayload(v: FormValues, folderIds: UUID[]): InviteCreate {
  const trim = (s: string | undefined): string | null => {
    const out = (s ?? "").trim();
    return out.length === 0 ? null : out;
  };
  return {
    email: v.email,
    role: v.role,
    employee_id:
      v.mode === "existing" && v.employee_id !== undefined && v.employee_id
        ? v.employee_id
        : null,
    employee:
      v.mode === "new"
        ? {
            full_name: (v.full_name ?? "").trim(),
            position: trim(v.position),
            department: trim(v.department),
          }
        : null,
    folder_ids: folderIds,
  };
}

export function InviteFormDialog({
  open,
  onClose,
  onCreated,
}: InviteFormDialogProps) {
  const { t } = useTranslation();
  const employees = useQuery({
    queryKey: ["employees", "for-invite"],
    queryFn: () => listEmployeesFull({ limit: 200 }),
    enabled: open,
  });
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY_FORM,
  });
  const mode = form.watch("mode");
  // Folder memberships live outside RHF (the FolderPicker owns a
  // UUID[] value/onChange). Validated on submit — at least one folder.
  const [folderIds, setFolderIds] = useState<UUID[]>([]);
  const [folderError, setFolderError] = useState(false);
  const [created, setCreated] = useState<InviteResponse | null>(null);
  const [copied, setCopied] = useState(false);
  // Clipboard write can return false (browser denied permission, jsdom in
  // tests, isolated frames). Surface that inline so the operator can still
  // copy the link manually instead of guessing why the button didn't latch.
  const [copyFailed, setCopyFailed] = useState(false);
  const mutation = useMutation({
    mutationFn: (v: FormValues) => createInvite(toInvitePayload(v, folderIds)),
    onSuccess: (resp) => {
      setCreated(resp);
      onCreated?.();
    },
  });

  useEffect(() => {
    if (!open) {
      setCreated(null);
      setCopied(false);
      setCopyFailed(false);
      setFolderIds([]);
      setFolderError(false);
      form.reset(EMPTY_FORM);
    }
  }, [open, form]);

  // Clear the folder-required flag as soon as the operator picks a folder.
  useEffect(() => {
    if (folderIds.length > 0) setFolderError(false);
  }, [folderIds]);

  const setMode = (next: EmployeeMode): void => {
    form.setValue("mode", next);
    // Drop the opposite branch's stale validation error so switching tabs
    // doesn't carry a red field the operator can no longer see.
    form.clearErrors(next === "new" ? "employee_id" : "full_name");
  };

  /**
   * If the operator hits "Close" before copying the token, the link is
   * irrecoverable (one-shot create response, never round-trips). A native
   * ``window.confirm`` is the lightest pattern that matches the rest of
   * the project (no existing toast/alert-dialog abstraction to reuse).
   *
   * Once ``copied`` is true the operator has the link in the clipboard; we
   * pass through without prompting so the happy path stays one click.
   */
  const handleCloseAfterCreate = (): void => {
    if (copied) {
      onClose();
      return;
    }
    const proceed =
      typeof window !== "undefined"
        ? window.confirm(t("adminUsers.invite.closeWithoutCopyConfirm"))
        : true;
    if (proceed) onClose();
  };

  const onSubmit = form.handleSubmit((v) => {
    if (folderIds.length === 0) {
      setFolderError(true);
      return;
    }
    mutation.mutate(v);
  });

  const inviteLink = useMemo(
    () => (created !== null ? buildInviteLink(created.token) : ""),
    [created],
  );
  // TM-1ac64335 follow-up: resolve the invite's folder ids to names so the
  // success screen can confirm «к каким папкам будет доступ» *before* the
  // invitee registers. Names come from the already-fetched folder list
  // (same source the FolderPicker uses) — no extra round-trip. Sorted by
  // name to match the registry's pending-invite chips.
  const { folders: allFolders } = useFolders();
  const grantedFolders = useMemo(() => {
    const ids = created?.folder_ids ?? [];
    const nameById = new Map(allFolders.map((f) => [f.id, f.name]));
    return ids
      .map((id) => ({ id, name: nameById.get(id) ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }, [created, allFolders]);
  // Backend blocks duplicate invites at create-time. The 409 detail flows
  // through ``parseErrorEnvelope`` into ``ApiError.code``, so map specific
  // codes to operator-friendly copy before falling back to the generic stub.
  const serverErrorMessage = mutation.isError
    ? mapCreateInviteError(mutation.error, t) ||
      toMessage(mutation.error, t("inviteCreate.errors.failed"))
    : "";

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (v) return;
        // The dialog "X" / overlay-click closes through ``onOpenChange``.
        // Apply the same confirm gate as the explicit Close button when
        // a token has been minted but not copied — otherwise a stray X
        // click would silently drop the link.
        if (created !== null) {
          handleCloseAfterCreate();
          return;
        }
        onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adminUsers.inviteButton")}</DialogTitle>
        </DialogHeader>
        {created === null ? (
          <form
            className="space-y-4"
            onSubmit={onSubmit}
            data-testid="invite-form"
          >
            {/* ---- Section: Employee ---- */}
            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold">
                {t("inviteCreate.employeeSection")}
              </legend>
              <div
                className="inline-flex rounded-md border p-0.5"
                role="tablist"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "new"}
                  className={cn(
                    "rounded px-3 py-1 text-sm",
                    mode === "new"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground",
                  )}
                  onClick={() => setMode("new")}
                  data-testid="invite-employee-mode-new"
                >
                  {t("inviteCreate.employeeModeNew")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "existing"}
                  className={cn(
                    "rounded px-3 py-1 text-sm",
                    mode === "existing"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground",
                  )}
                  onClick={() => setMode("existing")}
                  data-testid="invite-employee-mode-existing"
                >
                  {t("inviteCreate.employeeModeExisting")}
                </button>
              </div>

              {mode === "new" ? (
                <div className="space-y-2">
                  <div>
                    <Label htmlFor="invite-full-name">
                      {t("inviteCreate.fullNameLabel")}
                    </Label>
                    <Input
                      id="invite-full-name"
                      autoComplete="off"
                      aria-invalid={
                        form.formState.errors.full_name !== undefined
                      }
                      data-testid="invite-full-name"
                      {...form.register("full_name")}
                    />
                    {form.formState.errors.full_name !== undefined ? (
                      <p
                        className="text-xs text-destructive"
                        data-testid="invite-full-name-error"
                      >
                        {t("inviteCreate.fullNameRequired")}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label htmlFor="invite-position">
                      {t("inviteCreate.positionLabel")}
                    </Label>
                    <Input
                      id="invite-position"
                      autoComplete="off"
                      data-testid="invite-position"
                      {...form.register("position")}
                    />
                  </div>
                  <div>
                    <Label htmlFor="invite-department">
                      {t("inviteCreate.departmentLabel")}
                    </Label>
                    <Input
                      id="invite-department"
                      autoComplete="off"
                      data-testid="invite-department"
                      {...form.register("department")}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="invite-employee">
                    {t("inviteCreate.employeeSelectLabel")}
                  </Label>
                  <select
                    id="invite-employee"
                    className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                    aria-invalid={
                      form.formState.errors.employee_id !== undefined
                    }
                    data-testid="invite-employee"
                    {...form.register("employee_id")}
                  >
                    <option value="">
                      {t("inviteCreate.employeeSelectPlaceholder")}
                    </option>
                    {(employees.data ?? [])
                      .filter((e) => e.linked_user === null)
                      .map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.full_name}
                        </option>
                      ))}
                  </select>
                  {form.formState.errors.employee_id !== undefined ? (
                    <p
                      className="text-xs text-destructive"
                      data-testid="invite-employee-error"
                    >
                      {t("inviteCreate.employeeRequired")}
                    </p>
                  ) : null}
                </div>
              )}
            </fieldset>

            {/* ---- Section: Folders ---- */}
            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold">
                {t("inviteCreate.foldersSection")}
              </legend>
              <FolderPicker
                multi
                id="invite-folders"
                value={folderIds}
                onChange={setFolderIds}
                ariaInvalid={folderError}
              />
              {folderError ? (
                <p
                  className="text-xs text-destructive"
                  data-testid="invite-folders-error"
                >
                  {t("inviteCreate.foldersRequired")}
                </p>
              ) : null}
            </fieldset>

            {/* ---- Section: User ---- */}
            <fieldset className="space-y-2">
              <legend className="text-sm font-semibold">
                {t("inviteCreate.userSection")}
              </legend>
              <div>
                <Label htmlFor="invite-email">
                  {t("inviteCreate.emailLabel")}
                </Label>
                <Input
                  id="invite-email"
                  type="email"
                  autoComplete="off"
                  placeholder={t("inviteCreate.emailPlaceholder")}
                  aria-invalid={form.formState.errors.email !== undefined}
                  data-testid="invite-email"
                  {...form.register("email")}
                />
                {form.formState.errors.email !== undefined ? (
                  <p className="text-xs text-destructive">
                    {t("inviteCreate.emailRequired")}
                  </p>
                ) : null}
              </div>
              <div>
                <Label htmlFor="invite-role">
                  {t("inviteCreate.roleLabel")}
                </Label>
                <select
                  id="invite-role"
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  data-testid="invite-role"
                  {...form.register("role")}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            </fieldset>

            {mutation.isError ? (
              <p
                className="text-xs text-destructive"
                role="alert"
                data-testid="invite-error"
              >
                {serverErrorMessage}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={mutation.isPending}
                data-testid="invite-submit"
              >
                {mutation.isPending
                  ? t("common.saving")
                  : t("inviteCreate.submit")}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-3" data-testid="invite-token-box">
            <h3 className="text-sm font-semibold">
              {t("adminUsers.invite.linkTitle")}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t("adminUsers.invite.linkHint")}
            </p>
            <Input
              readOnly
              value={inviteLink}
              onFocus={(e) => e.currentTarget.select()}
              data-testid="invite-link-value"
            />
            {grantedFolders.length > 0 ? (
              <div
                className="flex flex-wrap items-center gap-1.5"
                data-testid="invite-granted-folders"
              >
                <span className="text-xs font-medium text-muted-foreground">
                  {t("adminUsers.invite.foldersAccessLabel")}
                </span>
                {grantedFolders.map((f) => (
                  <span
                    key={f.id}
                    className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200"
                    title={f.name}
                    data-testid={`invite-granted-folder-${f.id}`}
                  >
                    {f.name}
                  </span>
                ))}
              </div>
            ) : null}
            {copyFailed ? (
              <p
                className="text-xs text-destructive"
                role="alert"
                data-testid="invite-copy-error"
              >
                {t("adminUsers.invite.copyFailed")}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={async () => {
                  const ok = await copyToClipboard(inviteLink);
                  if (ok) {
                    setCopied(true);
                    setCopyFailed(false);
                  } else {
                    setCopyFailed(true);
                  }
                }}
                data-testid="invite-link-copy"
              >
                {copied
                  ? t("adminUsers.invite.copied")
                  : t("adminUsers.invite.copy")}
              </Button>
              <Button
                type="button"
                onClick={handleCloseAfterCreate}
                data-testid="invite-link-close"
              >
                {t("adminUsers.invite.close")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
